import { combineReducers, configureStore, Reducer } from '@reduxjs/toolkit';
import { conversationApi } from 'src/components/visionx/aio/reduxConfig/services/conversationService';
import { logout } from './actions';

// ---------------------------------------------------------------------------
// Core slices — eagerly loaded because they are used on EVERY page render
// (layout chrome, toasts, screen-size observer, global date picker, etc.).
// ---------------------------------------------------------------------------
import uiSlice from 'src/features/ui/uiSlice';
import devSlice from 'src/features/dev/devSlice';
import globalDatePickerSlice from 'src/components/common/navigations/extra-actions/GlobalDatePickerSlice';

// ---------------------------------------------------------------------------
// Eager reducers — always present in the store from app boot.
// ---------------------------------------------------------------------------
const eagerReducers = {
  uiSlice,
  devSlice,
  globalDatePickerSlice,
  [conversationApi.reducerPath]: conversationApi.reducer,
} as const;

// ---------------------------------------------------------------------------
// Lazy reducer registry.
// Feature slices call `injectReducers(...)` when their route chunk loads.
// ---------------------------------------------------------------------------
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazyReducers: Record<string, Reducer<any>> = {};

function buildCombinedReducer() {
  return combineReducers({ ...eagerReducers, ...lazyReducers });
}

let combinedReducer = buildCombinedReducer();

/**
 * Inject one or more reducers into the store at runtime.
 *
 * Call this at the top level of lazy-loaded route modules (outside the
 * component) so the slice is registered before the first render.
 *
 * Safe to call multiple times with the same key — duplicates are skipped.
 *
 * @example
 * ```ts
 * // At the top of a lazy-loaded route file:
 * import { injectReducers } from 'src/app/store';
 * import incidentsSlice from 'src/features/incidents/incidentsSlice';
 * import incidentsTableSlice from 'src/features/incidents/incidentsTableSlice';
 *
 * injectReducers({ incidentsSlice, incidentsTableSlice });
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function injectReducers(reducers: Record<string, Reducer<any>>): void {
  let hasNew = false;
  for (const [key, reducer] of Object.entries(reducers)) {
    if (!(key in lazyReducers) && !(key in eagerReducers)) {
      lazyReducers[key] = reducer;
      hasNew = true;
    }
  }
  if (hasNew) {
    combinedReducer = buildCombinedReducer();
    store.replaceReducer(rootReducer as typeof combinedReducer);
  }
}

// ---------------------------------------------------------------------------
// Root reducer — wraps the combined reducer with logout-reset logic.
// ---------------------------------------------------------------------------
const rootReducer = (
  state: ReturnType<typeof combinedReducer> | undefined,
  action: { type: string },
) => {
  if (action.type === logout.type) {
    state = undefined;
  }
  return combinedReducer(state, action);
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------
export const store = configureStore({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reducer: rootReducer as any,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(conversationApi.middleware),
});

// ---------------------------------------------------------------------------
// Type exports
// ---------------------------------------------------------------------------
export type { RootState } from './store.types';
export type AppDispatch = typeof store.dispatch;
